import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { visibleNavigation, isModuleActive } from '../../lib/navigation';
import { SidebarGroup } from './SidebarGroup';
import { SidebarItem } from './SidebarItem';
import { TooltipProvider } from '../ui/tooltip';

const KEY = (uid) => `lp.nav.groups.${uid || 'anon'}`;

export const SidebarNav = ({ collapsed, onNavigate }) => {
  const { user, can } = useAuth();
  const { pathname } = useLocation();
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY(user?.id)) || 'null') || {}; } catch { return {}; }
  });

  const categories = useMemo(() => visibleNavigation(can, user, collapsed ? '' : search), [can, user, search, collapsed]);
  const activeCat = useMemo(
    () => categories.find((c) => c.items.some((m) => isModuleActive(m, pathname)))?.id,
    [categories, pathname]
  );

  useEffect(() => {
    if (activeCat) setOpenGroups((g) => (g[activeCat] ? g : { ...g, [activeCat]: true }));
  }, [activeCat, pathname]);

  useEffect(() => {
    document.querySelector('[data-testid="sidebar-nav"] [aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
  }, [pathname, collapsed]);

  useEffect(() => {
    try { localStorage.setItem(KEY(user?.id), JSON.stringify(openGroups)); } catch { /* ignore */ }
  }, [openGroups, user?.id]);

  const toggle = (id) => setOpenGroups((g) => ({ ...g, [id]: !(g[id] ?? id === activeCat) }));
  const searching = !collapsed && search.trim().length > 0;

  return (
    <TooltipProvider>
      {!collapsed && (
        <div className="px-3 pt-3 pb-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar módulo…"
              aria-label="Buscar módulo"
              className="w-full h-9 pl-8 pr-8 rounded-md border border-input bg-background text-sm outline-none focus:ring-2 focus:ring-[hsl(var(--lifeport-red))]/30"
              data-testid="nav-search-input"
            />
            {searching && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted" aria-label="Limpar busca" data-testid="nav-search-clear">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}
      <nav className={`flex-1 overflow-y-auto overscroll-contain ${collapsed ? 'px-2 py-3' : 'px-3 pb-3 space-y-1'}`} data-testid="sidebar-nav" aria-label="Navegação principal">
        {categories.length === 0 && (
          <p className="px-3 py-6 text-xs text-muted-foreground text-center" data-testid="nav-empty">Nenhum módulo encontrado</p>
        )}
        {searching && !collapsed
          ? categories.flatMap((c) => c.items).map((m) => (
              <SidebarItem key={m.id} mod={m} active={isModuleActive(m, pathname)} onNavigate={onNavigate} />
            ))
          : categories.map((cat) => (
              <SidebarGroup
                key={cat.id}
                cat={cat}
                open={openGroups[cat.id] ?? cat.id === activeCat}
                onToggle={() => toggle(cat.id)}
                collapsed={collapsed}
                pathname={pathname}
                onNavigate={onNavigate}
              />
            ))}
      </nav>
    </TooltipProvider>
  );
};
